import storage from './storage';

/**
 * **😮切忌在公司能力级未初始化完成时调用**
 *
 * 判断是否有指定类型的公司能力级，多个能力级之间的关系为`||`
 * @param {*} filter 除`type`之外的其他限制
 * @param  {number|number[]} types
 * @param {(abilityValue, filterValue, filterKey) => boolean} match `filter`的匹配方法
 * @example ```
 * // 仅判断是否有某个公司能力级，不去判断这个公司能力级对应的value
 * hasAbilityByOr(143);
 * // 传递数组表示存在数组中任意一个即为 true
 * hasAbilityByOr([123, 124]);
 *
 * // 判断是否有某个公司能力级，并且这个公司能力级的value必须为 1
 * hasAbilityByOr({ value: '1' }, [95, 96])
 *
 * // 判断有公司能力级95 || 96，并且对应的value不为 1
 * hasAbilityByAnd({ value: '1' }, [95, 96], (a, b) => a !== b)
 * ```
 * @returns {boolean}
 */
export function hasAbilityByOr(filter, types, match) {
  const dictionary = storage.get(storage.NAME.DICTIONARY);
  if (!dictionary) {
    throw new Error('MONITOR_IGNORE 公司能力级未初始化');
  }

  if (arguments.length === 1) {
    types = filter;
    filter = undefined;
  }

  if (!Array.isArray(types)) {
    types = [types];
  }

  match = match || ((a, b) => a === b);

  const filterKeys = Object.keys(filter || {});

  for (const type of types) {
    const companyAbility = dictionary.find((item) => item.type === type);

    if (companyAbility) {
      if (!filterKeys.length) {
        // 没有过滤属性则表示只需要含有指定`type`即可，直接返回
        return true;
      }

      // 有过滤属性，则需要这一项的所有属性都符合要求，才算匹配通过
      let result = true;
      for (const key of filterKeys) {
        if (!match(companyAbility[key], filter[key], key)) {
          // 有一项过滤条件不匹配则表示该能力级不匹配，结束循环判断下一项
          result = false;
          break;
        }
      }

      if (result) {
        return true;
      }
    }
  }

  return false;
}

/**
 * **😮切忌在公司能力级未初始化完成时调用**
 *
 * 判断是否有指定类型的公司能力级，多个能力级之间的关系为`&&`
 * @param {*} filter 除`type`之外的其他限制
 * @param  {number|number[]} types
 * @param {(abilityValue, filterValue, filterKey) => boolean} match `filter`的匹配方法
 * @example ```
 * // 仅判断是否有某个公司能力级，不去判断这个公司能力级对应的value
 * hasAbilityByAnd(143);
 * // 传递数组表示存在数组中所有能力级才为 true
 * hasAbilityByAnd([123, 124]);
 *
 * // 判断同时有公司能力级95, 96，并且对应的value为 1
 * hasAbilityByAnd({ value: '1' }, [95, 96])
 *
 * // 判断同时有公司能力级95, 96，并且对应的value不为 1
 * hasAbilityByAnd({ value: '1' }, [95, 96], (a, b) => a !== b)
 * ```
 * @returns {boolean}
 */
export function hasAbilityByAnd(filter, types, match) {
  const dictionary = storage.get(storage.NAME.DICTIONARY);
  if (!dictionary) {
    throw new Error('MONITOR_IGNORE 公司能力级未初始化');
  }

  if (arguments.length === 1) {
    types = filter;
    filter = undefined;
  }

  if (!Array.isArray(types)) {
    types = [types];
  }

  match = match || ((a, b) => a === b);

  const filterKeys = Object.keys(filter || {});

  for (const type of types) {
    const companyAbility = dictionary.find((item) => item.type === type);

    if (companyAbility) {
      if (!filterKeys.length) {
        // 没有过滤属性则表示只需要含有指定`type`即可，继续判断是否有下一个`type`
        continue;
      }

      // 有过滤属性，则需要这一项的所有属性都符合要求，才算匹配通过
      for (const key of filterKeys) {
        if (!match(companyAbility[key], filter[key], key)) {
          // 有一项过滤条件不匹配则表示该能力级不匹配，进而表示匹配不通过，直接返回`false`
          return false;
        }
      }
    } else {
      // 任何一个`type`的公司能力级不存在，则直接返回`false`
      return false;
    }
  }

  return true;
}

/**
 * 判断是否有指定类型的公司能力级
 * @param  {number} type
 * @returns {boolean}
 */
export function hasCurAbility(type) {
  const dictionary = storage.get(storage.NAME.DICTIONARY);
  if (!dictionary) {
    throw new Error('MONITOR_IGNORE 公司能力级为初始化');
  }
  const companyAbility = dictionary.find((item) => item.type === type);
  if (companyAbility) {
    return true;
  }
  return false;
}

/**
 * 能力级的值是json数组字符串，判断数组中是否存在匹配的值
 */
export function matchJsonArrayOr(abilityValue, filterValue) {
  const parsedValue = JSON.parse(abilityValue);

  if (Array.isArray(parsedValue)) {
    return parsedValue.some((item) => item === filterValue);
  }

  return abilityValue === filterValue;
}

/**
 * 根据顺序依次判断能力级，根据value返回结果
 * @param  {...number} types
 * @example ```javascript
// 公司没配，国家配了，以国家的为准， 公司配了，国家配了或者没配，以公司的为主
// 158是是国家维度，159是公司维度
hasAbilityByWeight(159, 158);
```
 * @returns {boolean}
 */
export function hasAbilityByWeight(...types) {
  const dictionary = storage.get(storage.NAME.DICTIONARY);
  if (!dictionary) {
    throw new Error('MONITOR_IGNORE 公司能力级未初始化');
  }

  for (const type of types) {
    const companyAbility = dictionary.find((item) => item.type === type);
    if (companyAbility) {
      return companyAbility.value === '1';
    }
  }
  return false;
}

/**
 * 能力集接口兼容HCC能力，对应灰度不是数字枚举而是字符串，使用新方法匹配(服务端修改返回，废弃)
 * @param  {string} key
 * @example ```判断当前能力集中是否包含对应字符串类型的灰度
```
 * @returns {boolean}
 */
export function hasAbilityByKey(key) {
  const dictionary = storage.get(storage.NAME.DICTIONARY);
  if (!dictionary) {
    throw new Error('MONITOR_IGNORE 公司能力级未初始化');
  }
  const companyAbility = dictionary.find((item) => item.grayType === key);
  if (companyAbility) {
    return companyAbility.ability === 1;
  }
  return false;
}

